/**
 * Austausch mit der Verwaltung — die Rückfragen.
 *
 * Drei Reiter wie in der Vorlage: Alle, Offen, Beantwortet. Jede Rückfrage
 * sagt, woran sie hängt — Beleg, Kontoauszugsposition, Unterlage oder ein
 * Prüfschritt — und führt mit einem Klick dorthin.
 *
 * Die Verwaltung antwortet, der Beirat markiert als erledigt. Nicht
 * umgekehrt: Eine Frage ist erst beantwortet, wenn der Fragende es sagt.
 */

import { useMemo, useState } from 'react';

import { api, datum, eur } from '../../lib/api';
import { markierungLesen } from '../../lib/typen';
import type {
  PdfRechteck, Banktransaktion, Beleg, Dokument, Kategorie, Kommentar, Rolle, Schritt } from '../../lib/typen';

export interface RueckfragenProps {
  pruefungId: string;
  kommentare: Kommentar[];
  belege: Beleg[];
  transaktionen: Banktransaktion[];
  dokumente: Dokument[];
  kategorien: Kategorie[];
  schritte: Schritt[];
  rolle: Rolle;
  abgeschlossen: boolean;
  onBeleg: (belegId: string) => void;
  onDokument: (dokumentId: string, stelle?: PdfRechteck | null) => void;
  onSchritt: (nummer: number) => void;
  onPosition: (transaktionId: string) => void;
  onNeuLaden: () => void;
  onMeldung: (text: string) => void;
}

type Reiter = 'alle' | 'offen' | 'beantwortet';

export function Rueckfragen({
  pruefungId,
  kommentare,
  belege,
  transaktionen,
  dokumente,
  kategorien,
  schritte,
  rolle,
  abgeschlossen,
  onBeleg,
  onDokument,
  onSchritt,
  onPosition,
  onNeuLaden,
  onMeldung,
}: RueckfragenProps) {
  const [reiter, setReiter] = useState<Reiter>('alle');
  const [antwortAuf, setAntwortAuf] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState('');

  const belegNach = useMemo(() => new Map(belege.map((b) => [b.id, b])), [belege]);
  const dokNach = useMemo(() => new Map(dokumente.map((d) => [d.id, d])), [dokumente]);
  const katNach = useMemo(() => new Map(kategorien.map((k) => [k.id, k])), [kategorien]);
  const txNach = useMemo(() => new Map(transaktionen.map((t) => [t.id, t])), [transaktionen]);

  const straenge = useMemo(() => {
    const rang = { offen: 0, beantwortet: 1, erledigt: 2 };
    return kommentare
      .filter((k) => !k.eltern_id)
      .map((wurzel) => ({ wurzel, antworten: kommentare.filter((k) => k.eltern_id === wurzel.id) }))
      .sort((a, b) => rang[a.wurzel.status] - rang[b.wurzel.status] || b.wurzel.erstellt_am.localeCompare(a.wurzel.erstellt_am));
  }, [kommentare]);

  const offene = straenge.filter((s) => s.wurzel.status === 'offen').length;
  const beantwortete = straenge.filter((s) => s.wurzel.status === 'beantwortet').length;
  const sichtbar = straenge.filter((s) =>
    reiter === 'alle' ? true : reiter === 'offen' ? s.wurzel.status === 'offen' : s.wurzel.status !== 'offen',
  );

  async function antworten(elternId: string) {
    if (!entwurf.trim()) return;
    try {
      await api.kommentarAnlegen(pruefungId, { elternId, text: entwurf.trim() });
      setEntwurf('');
      setAntwortAuf(null);
      onNeuLaden();
      onMeldung('Antwort gesendet.');
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function erledigen(id: string) {
    try {
      await api.kommentarErledigen(id);
      onNeuLaden();
      onMeldung('Rückfrage erledigt.');
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  /** Woran die Rückfrage hängt — Text und Sprungziel. */
  function anker(k: Kommentar): { text: string; detail?: string; zu?: () => void } {
    if (k.beleg_id) {
      const b = belegNach.get(k.beleg_id);
      return {
        text: b ? `Beleg · ${b.lieferant ?? 'Ohne Lieferant'}` : 'Beleg',
        detail: b ? `${b.buchungszeilen[0]?.sachkontoNummer ?? '—'} · ${eur(b.brutto)}` : undefined,
        zu: () => onBeleg(k.beleg_id!),
      };
    }
    if (k.transaktion_id) {
      const t = txNach.get(k.transaktion_id);
      return {
        text: t ? `Abbuchung · ${t.partnerName ?? 'ohne Name'}` : 'Kontoauszugsposition',
        detail: t ? `${datum(t.buchungsdatum)} · ${eur(t.betrag)}` : undefined,
        zu: () => onPosition(k.transaktion_id!),
      };
    }
    if (k.dokument_id) {
      const d = dokNach.get(k.dokument_id);
      const kat = d ? katNach.get(d.kategorieId) : undefined;
      return {
        text: kat ? `Unterlage · ${kat.bezeichnung}` : 'Unterlage',
        detail: d?.dateiname,
        zu: d ? () => onDokument(d.id, markierungLesen(k)) : undefined,
      };
    }
    if (k.schritt) {
      const s = schritte.find((x) => x.nummer === k.schritt);
      return { text: `Schritt ${k.schritt} · ${s?.titel ?? ''}`, zu: () => onSchritt(k.schritt!) };
    }
    return { text: 'Allgemein' };
  }

  return (
    <div className="wrap" style={{ maxWidth: 920 }}>
      <div className="seitenkopf">
        <div>
          <h2 style={{ margin: 0 }}>Austausch mit der Verwaltung</h2>
          <p className="s" style={{ margin: '4px 0 0' }}>
            Rückfragen stellen Sie direkt am Beleg, an der Unterlage oder am Prüfschritt. Hier laufen alle zusammen.
          </p>
        </div>
      </div>

      <div className="chips" role="tablist" style={{ marginBottom: 14 }}>
        <button className="fchip" role="tab" aria-pressed={reiter === 'alle'} onClick={() => setReiter('alle')}>
          Alle Fragen <span className="badge">{straenge.length}</span>
        </button>
        <button className="fchip" role="tab" aria-pressed={reiter === 'offen'} onClick={() => setReiter('offen')}>
          Offen {offene > 0 && <span className="badge w">{offene}</span>}
        </button>
        <button className="fchip" role="tab" aria-pressed={reiter === 'beantwortet'} onClick={() => setReiter('beantwortet')}>
          Beantwortet {beantwortete > 0 && <span className="badge">{beantwortete}</span>}
        </button>
      </div>

      {sichtbar.length === 0 ? (
        <div className="leer card">
          <h3>{straenge.length === 0 ? 'Keine Rückfragen' : 'Nichts in dieser Auswahl'}</h3>
          <p>
            {straenge.length === 0
              ? 'Bislang wurde keine Rückfrage gestellt. Öffnen Sie einen Beleg oder eine Unterlage und wählen Sie „Rückfrage an Verwaltung".'
              : 'Wechseln Sie den Reiter, um die übrigen zu sehen.'}
          </p>
        </div>
      ) : (
        sichtbar.map(({ wurzel, antworten: antw }) => {
          const a = anker(wurzel);
          const pille =
            wurzel.status === 'offen'
              ? { klasse: 'w', text: 'Offen' }
              : wurzel.status === 'beantwortet'
                ? { klasse: 'n', text: 'Beantwortet' }
                : { klasse: 'ok', text: 'Erledigt' };
          return (
            <div key={wurzel.id} className="card qcard">
              <div className="thumb" />
              <div className="body">
                <div className="qmeta">
                  <span className={`pill ${pille.klasse}`}>
                    <span className="dot" />
                    {pille.text}
                  </span>
                  <span>{a.text}</span>
                  {a.detail && <span className="mono">{a.detail}</span>}
                </div>
                <p className="qtext">{wurzel.text}</p>
                <div style={{ fontSize: 11.5, color: 'var(--tx-3)', marginBottom: 8 }}>
                  {wurzel.autor_name} · {datum(wurzel.erstellt_am)}
                </div>

                {antw.map((x) => (
                  <div key={x.id} className="antwort">
                    {x.text}
                    <div style={{ fontSize: 11, color: 'var(--tx-3)', marginTop: 4 }}>
                      {x.autor_name} · {datum(x.erstellt_am)}
                    </div>
                  </div>
                ))}

                {antwortAuf === wurzel.id ? (
                  <div style={{ marginTop: 10 }}>
                    <textarea value={entwurf} onChange={(e) => setEntwurf(e.target.value)} placeholder="Antwort an den Beirat …" autoFocus />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button className="btn sm" onClick={() => antworten(wurzel.id)}>
                        Antwort senden
                      </button>
                      <button className="btn ghost sm" onClick={() => setAntwortAuf(null)}>
                        Abbrechen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                    {a.zu && (
                      <button className="btn ghost sm" onClick={a.zu}>
                        Öffnen
                      </button>
                    )}
                    {rolle === 'verwaltung' && wurzel.status !== 'erledigt' && !abgeschlossen && (
                      <button className="btn ghost sm" onClick={() => setAntwortAuf(wurzel.id)}>
                        Antwort schreiben
                      </button>
                    )}
                    {rolle === 'beirat' && wurzel.status === 'beantwortet' && !abgeschlossen && (
                      <button className="btn ok sm" onClick={() => erledigen(wurzel.id)}>
                        Als erledigt markieren
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
